import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Landmark, ArrowLeft, Upload, Sparkles, Loader2, RotateCw, Trash2, ExternalLink, Layers, Database, Pin, PinOff, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { canManageInfraFunds } from '../lib/access';
import { FundProfile } from '../components/FundScreenerCard';
import { DealImportDialog } from '../components/DealImportDialog';
import { InfraDealsTable, CategoryBadge } from '../components/InfraDealsTable';
import { useScreenRun, ScreenResults } from '../components/InfraScreenResults';
import { useRowActions } from '../components/useRowActions';
import { IIImportPanel } from '../components/IIImportPanel';
import { IIProfileSection } from '../components/IIProfileSection';
import { WorkbookFacts } from '../components/WorkbookFacts';
import { useInfraScreenFilters, InfraScreenFilters, useThemeSelection } from '../components/InfraScreenFilters';
import { buildExploreBody } from '../components/DealFilterPanel';

const EST_USD = 0.12;
const PHASE = { searching: 'Web-Recherche (Perplexity)…', crawling: 'Fonds-Website wird gelesen…', profiling: 'Investment-Profil wird erstellt…' };
const STATUS = {
  ready: ['Profil fertig', 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'],
  researching: ['Recherche läuft', 'text-amber-300 border-amber-500/40 bg-amber-500/10'],
  failed: ['Recherche fehlgeschlagen', 'text-red-400 border-red-500/40 bg-red-500/10'],
  new: ['Noch kein Profil', 'text-muted-foreground border-border bg-secondary'],
};
const fmtUsd = (v) => `$${Number(v || 0).toFixed(2)}`;

function InlineConfirm({ text, onYes, onNo, testid }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-foreground" data-testid={testid}>
      {text}
      <button onClick={onYes} className="px-2 h-6 rounded-sm bg-amber-600 hover:bg-amber-500 text-white font-medium" data-testid={`${testid}-yes`}>Ja, starten</button>
      <button onClick={onNo} className="px-2 h-6 rounded-sm border border-border text-foreground" data-testid={`${testid}-no`}>Nein</button>
    </span>
  );
}

function FundHeader({ fund, onResearch, onDelete, onPin, canManage }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const [label, cls] = STATUS[fund.status] || STATUS.new;
  const researching = fund.status === 'researching';
  return (
    <div className="space-y-2" data-testid="infra-fund-header">
      <Link to="/infra-funds" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="infra-fund-back">
        <ArrowLeft className="w-3 h-3" /> Alle Infra-Fonds
      </Link>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl text-foreground tracking-tight flex items-center gap-2" data-testid="infra-fund-name">
            <Landmark className="w-6 h-6 text-amber-400 shrink-0" /> {fund.name}
            {fund.pinned && <Pin className="w-4 h-4 text-amber-400 shrink-0" fill="currentColor" title={`Angepinnt${fund.pinned_by_name ? ` von ${fund.pinned_by_name}` : ''}`} />}
          </h1>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className={`text-[9px] font-medium border rounded-sm px-1.5 py-px ${cls}`} data-testid="infra-fund-status">{label}</span>
            {fund.website && (
              <a href={fund.website.startsWith('http') ? fund.website : `https://${fund.website}`} target="_blank" rel="noopener noreferrer"
                className="text-[11px] text-sky-400/80 hover:text-sky-300 inline-flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> {fund.website.replace(/^https?:\/\/(www\.)?/, '')}{fund.website_guessed ? ' (erkannt)' : ''}
              </a>
            )}
            {fund.cost_usd != null && <span className="text-[10px] text-muted-foreground/60">Profil-Recherche {fmtUsd(fund.cost_usd)}</span>}
            {fund.research_warning && <span className="text-[10px] text-amber-300/80">{fund.research_warning}</span>}
            {fund.status === 'failed' && <span className="text-[10px] text-red-400" data-testid="fund-research-error">{fund.error || 'Grund unbekannt — bitte „Profil recherchieren“ erneut starten'}</span>}
          </div>
          {researching && (
            <p className="text-[11px] text-amber-300/90 inline-flex items-center gap-1.5 mt-1" data-testid="infra-fund-research-progress">
              <Loader2 className="w-3 h-3 animate-spin" /> {PHASE[fund.phase] || 'Recherche läuft…'}{fund.phase === 'searching' && ` ${fund.research_progress || 0}/3`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!canManage && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-sky-300 border border-sky-500/30 rounded-sm px-2.5 h-8" data-testid="infra-fund-screen-only-badge"
              title="Stufe „nur Deals suchen“: Screening der Datenbank gegen diesen Fonds ist möglich — Profil-Research, Deal-Import/-Recherche, Löschen und Pinnen übernimmt der Admin">
              <Lock className="w-3 h-3" /> nur Deals suchen
            </span>
          )}
          {canManage && (
          <>
          <button onClick={onPin} data-testid="infra-fund-pin-btn" title={fund.pinned ? 'Vom Seitenanfang lösen' : 'In der Fonds-Liste oben anpinnen'}
            className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 border ${fund.pinned ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-border text-foreground hover:border-amber-500/40'}`}>
            {fund.pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />} {fund.pinned ? 'Angepinnt' : 'Anpinnen'}
          </button>
          <button onClick={onResearch} disabled={researching} data-testid="infra-fund-research-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 border border-border text-foreground hover:border-amber-500/40 disabled:opacity-50">
            <RotateCw className={`w-3.5 h-3.5 ${researching ? 'animate-spin' : ''}`} /> {fund.profile ? 'Profil aktualisieren' : 'Profil recherchieren'}
          </button>
          {confirmDel ? (
            <span className="inline-flex items-center gap-1 text-[11px]" data-testid="infra-fund-delete-confirm">
              Fonds + Deals löschen?
              <button onClick={onDelete} className="px-2 h-6 rounded-sm bg-red-600 hover:bg-red-500 text-white" data-testid="infra-fund-delete-yes">Ja</button>
              <button onClick={() => setConfirmDel(false)} className="px-2 h-6 rounded-sm border border-border">Nein</button>
            </span>
          ) : (
            <button onClick={() => setConfirmDel(true)} title="Fonds löschen" data-testid="infra-fund-delete-btn"
              className="w-8 h-8 inline-flex items-center justify-center text-muted-foreground hover:text-red-400 border border-border rounded-sm"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

function DealResearchBar({ deals, job, onStart, onImport, canManage }) {
  const [confirm, setConfirm] = useState(null);
  const open = deals.filter(d => d.research?.status !== 'done').length;
  const running = job?.status === 'running';
  const progress = running && job.total ? Math.round(((job.done || 0) + (job.failed || 0)) / job.total * 100) : 0;
  if (!canManage) {
    return (
      <div className="bg-card border border-border rounded-sm px-4 py-3" data-testid="deal-research-bar">
        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 inline-flex items-center gap-1.5"><Database className="w-3 h-3" /> Deals des Fonds</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          <span className="text-foreground font-medium" data-testid="deal-counts">{deals.length} Deals · {deals.length - open} recherchiert</span>
          {' — '}recherchierte Deals aufklappen und „Firmen für diesen Deal finden“ starten. Neue Deals einspielen oder recherchieren (kostenpflichtig) übernimmt der Admin.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border rounded-sm px-4 py-3 space-y-2" data-testid="deal-research-bar">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 inline-flex items-center gap-1.5"><Database className="w-3 h-3" /> Deals des Fonds</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            <span className="text-foreground font-medium" data-testid="deal-counts">{deals.length} Deals · {deals.length - open} recherchiert</span>
            {' — '}die KI (Perplexity + Claude) recherchiert pro Deal das Target, seine Zukäufe und leitet angrenzende Geschäftsmodelle mit Suchbegriffen für unsere Datenbank ab.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onImport} data-testid="deal-import-open-btn"
            className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 border border-border text-foreground hover:border-amber-500/40">
            <Upload className="w-3.5 h-3.5" /> Deals einspielen
          </button>
          {confirm ? (
            <InlineConfirm testid="deal-research-confirm" onNo={() => setConfirm(null)} onYes={() => { onStart(confirm === 'force'); setConfirm(null); }}
              text={`${confirm === 'force' ? deals.length : open} Deals · geschätzt ≈ ${fmtUsd((confirm === 'force' ? deals.length : open) * EST_USD)}.`} />
          ) : (
            <>
              <button onClick={() => setConfirm('open')} disabled={running || !open} data-testid="deal-research-btn"
                className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50">
                {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {running ? 'Recherche läuft…' : open ? `${open} offene Deals recherchieren · ≈ ${fmtUsd(open * EST_USD)}` : 'Alle Deals recherchiert'}
              </button>
              {!running && deals.length > 0 && deals.length !== open && (
                <button onClick={() => setConfirm('force')} data-testid="deal-research-force-btn" title="Alle Deals erneut recherchieren"
                  className="text-[11px] text-muted-foreground hover:text-foreground underline underline-offset-2">alle erneut</button>
              )}
            </>
          )}
        </div>
      </div>
      {running && (
        <div data-testid="deal-research-progress">
          <div className="h-1.5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} /></div>
          <p className="text-[10px] text-muted-foreground mt-1">
            {(job.done || 0) + (job.failed || 0)} / {job.total} Deals · bisher {fmtUsd(job.cost_usd)}{job.failed ? ` · ${job.failed} fehlgeschlagen` : ''}
          </p>
        </div>
      )}
      {!running && job?.status === 'done' && (
        <p className="text-[10px] text-muted-foreground" data-testid="deal-research-done">
          Letzte Recherche: {job.done} Deals · {fmtUsd(job.cost_usd)}{job.failed ? ` · ${job.failed} fehlgeschlagen (↻ in der Zeile erneut starten)` : ''}
        </p>
      )}
      {!running && job?.status === 'failed' && <p className="text-[10px] text-red-400" data-testid="deal-research-failed">{job.error || 'Recherche fehlgeschlagen'}</p>}
    </div>
  );
}

function FundAngles({ fundId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.infraFundAngles(fundId).then(r => setData(r.data)).catch(() => {}); }, [fundId, refreshKey]);
  if (!data || !data.angles.length) return null;
  return (
    <div className="bg-card border border-border rounded-sm" data-testid="fund-angles">
      <div className="px-4 py-3 border-b border-border">
        <p className="text-[10px] uppercase tracking-widest font-bold text-sky-300/80 inline-flex items-center gap-1.5"><Layers className="w-3 h-3" /> Angrenzende Geschäftsmodelle aus {data.researched} recherchierten Deals</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">Gebündelt über alle Deals des Fonds — fließen automatisch in das Fonds-Screening unten ein.</p>
      </div>
      <div className="divide-y divide-border/40 max-h-[320px] overflow-y-auto">
        {data.angles.map((a, i) => (
          <div key={i} className="flex items-start gap-3 px-4 py-2" data-testid={`fund-angle-${i}`}>
            <span className="text-sm font-mono font-bold text-sky-300 w-8 shrink-0">{a.count}×</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap"><span className="text-xs text-foreground font-medium">{a.label}</span><CategoryBadge category={a.category} /></div>
              {a.descriptions?.[0] && <p className="text-[11px] text-muted-foreground leading-snug">{a.descriptions[0]}</p>}
              <p className="text-[10px] text-muted-foreground/70 truncate">aus: {a.deals.map(d => d.target).join(' · ')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InfraFundDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = canManageInfraFunds(user);
  const [data, setData] = useState(null);
  const [dealsData, setDealsData] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [missing, setMissing] = useState(false);
  const rowActions = useRowActions(`/infra-funds/${id}`);
  const screen = useScreenRun(data ? { run: data.run, rows: data.rows } : null);

  const loadFund = useCallback(() => api.fundGet(id).then(r => setData(r.data)).catch(e => { if (e.response?.status === 404) setMissing(true); }), [id]);
  const loadDeals = useCallback(() => api.infraDeals(id).then(r => setDealsData(r.data)).catch(() => {}), [id]);
  useEffect(() => { loadFund(); loadDeals(); }, [loadFund, loadDeals]);

  const fund = data?.fund;
  const deals = dealsData?.deals || [];
  const job = dealsData?.job;
  const busy = fund?.status === 'researching' || job?.status === 'running';
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(() => { loadFund(); loadDeals(); }, 4000);
    return () => clearInterval(t);
  }, [busy, loadFund, loadDeals]);

  const startDealResearch = async (force) => {
    try {
      const r = await api.infraResearchDeals(id, { force });
      toast.success(`Recherche für ${r.data.total} Deals gestartet (≈ ${fmtUsd(r.data.estimate_usd)})`);
      loadDeals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };
  const researchProfile = async () => {
    try { await api.fundResearch(id, fund.website, {}); toast.success('Profil-Recherche gestartet — Deals aus der Datenbank fließen ein'); loadFund(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Start fehlgeschlagen'); }
  };
  const togglePin = async () => {
    try { await api.fundPin(id, !fund.pinned); toast.success(fund.pinned ? 'Pin gelöst' : 'Fonds steht jetzt oben in der Liste'); loadFund(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Anpinnen fehlgeschlagen'); }
  };
  const removeFund = async () => {
    try { await api.fundDelete(id); toast.success('Fonds gelöscht'); navigate('/infra-funds'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };
  const sf = useInfraScreenFilters();
  const themes = useThemeSelection(data?.themes);
  const screenFund = () => screen.start(api.fundScreen(id, sf.body(), themes.selected));

  if (missing) return <p className="text-sm text-muted-foreground py-10 text-center" data-testid="infra-fund-missing">Fonds nicht gefunden. <Link to="/infra-funds" className="underline">Zur Übersicht</Link></p>;
  if (!fund) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  const researched = deals.filter(d => d.research?.status === 'done').length;

  return (
    <div className="space-y-4" data-testid="infra-fund-detail-page">
      <FundHeader fund={fund} onResearch={researchProfile} onDelete={removeFund} onPin={togglePin} canManage={canManage} />
      {fund.workbook && <WorkbookFacts wb={fund.workbook} />}
      {fund.ii && <IIProfileSection fund={fund} onChanged={loadFund} />}
      {canManage && <IIImportPanel fund={fund} onSaved={() => { loadFund(); loadDeals(); }} defaultOpen={!fund.ii && !fund.workbook} />}
      {fund.summary && <p className="text-sm text-muted-foreground leading-relaxed" data-testid="infra-fund-summary">{fund.summary}</p>}
      {fund.profile && <FundProfile fund={fund} defaultOpen={false} />}

      <DealResearchBar deals={deals} job={job} onStart={startDealResearch} onImport={() => setImportOpen(true)} canManage={canManage} />
      <div className="bg-card border border-border rounded-sm">
        {dealsData ? (
          <InfraDealsTable deals={deals} fundName={fund.name} onChanged={loadDeals} rowProps={rowActions.rowProps} decorate={rowActions.decorate} screenFilters={sf.body} canManage={canManage} />
        ) : <div className="flex justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
      </div>

      <FundAngles fundId={id} refreshKey={researched} />

      <InfraScreenFilters f={sf} prefix="fund" appliesTo="„Datenbank screenen“ und das Screening einzelner Deals" themes={themes} fundId={id} />
      <div className="bg-card border border-border rounded-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="fund-screen-bar">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300/80 inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Firmen für diesen Fonds finden</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Screent unsere Datenbank nach dem Investment-Profil{researched ? ` plus den Angles aus ${researched} recherchierten Deals` : ''} — Kern-Target, angrenzend oder Add-on zu einem Portfolio-Unternehmen.
            {' '}<span className="text-amber-300/80" data-testid="fund-screen-stage-hint">Ohne Stage-Filter nur Firmen in Succession Possible und OLTA TAM — keine passed Deals, nichts, was schon im Prozess ist.</span>
            {themes.selected.length > 0 && <span className="text-amber-200" data-testid="fund-screen-theme-hint"> · nur {themes.selected.length} {themes.selected.length !== 1 ? 'Themen' : 'Thema'}: {themes.labels.join(', ')}</span>}
            {sf.active.length > 0 && <span className="text-amber-200" data-testid="fund-screen-filter-hint"> · {sf.active.length} Filter aktiv</span>}
          </p>
        </div>
        <button onClick={screenFund} disabled={fund.status !== 'ready' || screen.run?.status === 'running'} data-testid="fund-screen-btn"
          title={fund.status !== 'ready' ? (canManage ? 'Erst das Profil recherchieren' : 'Profil noch nicht recherchiert — bitte den Admin') : ''}
          className="inline-flex items-center gap-1.5 text-xs font-medium rounded-sm px-3 h-8 bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50 shrink-0">
          {screen.run?.status === 'running' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Datenbank screenen
        </button>
      </div>
      <ScreenResults run={screen.run} rows={screen.rows} label={`für ${fund.name}`} prefix="fundpage" onRescreen={screenFund} onReset={screen.reset} currentFilters={buildExploreBody(sf.filters)} currentThemes={themes.selected}
        rowProps={rowActions.rowProps} decorate={rowActions.decorate} fundName={fund.name} />

      {canManage && <DealImportDialog fundId={id} fundName={fund.name} open={importOpen} onClose={() => setImportOpen(false)} onSaved={loadDeals} />}
      {rowActions.passDialog}
    </div>
  );
}
